import { Component, ElementRef, ViewChild, ChangeDetectionStrategy, signal } from "@angular/core";
import { Router } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { MAT_DATE_FORMATS } from "@angular/material/core";
import { UntypedFormControl, FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { AbstractBuyerComponent } from "./abstract/buyer.component";
import { EntityBase } from "../entity/EntityBase";
import { AuthService } from "../service/auth.service";
import { AnalyticsService } from "../service/analytics.service";
import { GeneralService } from "../service/general.service";
import { ValidationService } from "../service/validation.service";
import { Account } from "../entity/Account";
import { Utility } from "../entity/Utility";
import { ExistingContract } from "../entity/ExistingContract";
import { User } from "../entity/User";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ConfirmationDialog } from "./incs/confirmation-dialog.component";
import { AccountExampleDialog } from "./incs/account-example-dialog.component";
import { LoadZone } from "../entity/LoadZone";
import { RateClass } from "../entity/RateClass";
import { AccountConsumption } from "../obj/AccountConsumption";
import { Company } from "../entity/Company";
import { LoaderService } from "../service/loader.service";
import { environment } from "../../environments/environment";
import { HeaderComponent } from "./incs/header.component";




import { firstValueFrom } from "rxjs";
import { AccountAddStep2Component } from "./incs/account-add-step2.component";
import { AccountAddStep3Component } from "./incs/account-add-step3.component";

export const MY_FORMATS = {
    parse: {
        dateInput: "MM/dd/yyyy",
    },
    display: {
        dateInput: "MM/dd/yyyy",
        monthYearLabel: "MMM yyyy",
        dateA11yLabel: "LL",
        monthYearA11yLabel: "MMMM yyyy",
    },
};

@Component({
    selector: "kilowant-account-add",
    templateUrl: "../../html/main-account-add.html",
    providers: [
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS }
    ],
    imports: [HeaderComponent, FormsModule, ReactiveFormsModule, AccountAddStep2Component, AccountAddStep3Component],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountAddComponent extends AbstractBuyerComponent {
    @ViewChild('serviceName') serviceName: ElementRef;
    welcomeText: boolean = false;
    setWelcomeForMyKilowant: boolean = false;
    account: Account = new Account();
    consumptions: AccountConsumption[] = [];
    utilities: Utility[] = [];
    rateClasses: RateClass[] = [];
    currentAccountNumber: FormControl = new FormControl("");
    loadZones: LoadZone[] = [];
    utilityId: number | null = null;
    imageMode: number = 0;
    selectCompany: boolean = false;
    addCompanyName: boolean = false;

    dialogRef: MatDialogRef<ConfirmationDialog> = null!;
    existingContractId: number | null = null;
    confirmButtonActive: boolean = true;

    utilityRate: number | null = null;
    communityRate: number | null = null;
    setStartingContract: number | null = null;
    existingContract: ExistingContract|null = new ExistingContract();

    termFrom = new UntypedFormControl();
    termTo = new UntypedFormControl();

    step: number = 1;

    fileToUpload: File | null = null;
    billsToUpload: File[] = [];
    deleteTaxExemptCertificateDocument: boolean = false;
    defaultShowAccountInfo: boolean = true;

    toBeUsedUser: User;

    loading: boolean = false;
    progress: boolean = false;
    progressWidth: number = 0;

    userCompanies: Company[];

    disableSelectorOfUtilityData: boolean = false;
    newCompanyRegistration = signal<boolean>(false);
    taxExemptAllowToAdd = signal<boolean>(true);
    taxExempt: boolean = false;
    warningCommercial: boolean = false;

    constructor(
        router: Router,
        route: ActivatedRoute,
        authService: AuthService,
        protected loaderService: LoaderService,
        analyticsService: AnalyticsService,
        generalService: GeneralService,
        public validationService: ValidationService,
        dialog: MatDialog
    ) {
        super(router, route, dialog, authService, analyticsService, generalService);
    }

    ngOnInit(): void {
        this.initPromises.push(
            this.generalService.getUtilities(this.generalService.getDefaultFuelType(), true, false, null).then((utilities) => {
                this.utilities = utilities;
                this.account.fuelType = this.generalService.getDefaultFuelType();
                let u = this.generalService.getDefaultUtilityFromList(this.utilities);
                this.account.utility = u;
                if (u != null) {
                    this.utilityId = u.id;
                }
                this.refreshView();
            })
        );
        super.ngOnInit();
    }

    pageStart(): void {
        if (this.router.url === "/account/onboarding") {
            this.welcomeText = true;
            this.setWelcomeForMyKilowant = true;
        }
        if (this.user.isAdvisor) {
            let client = this.generalService.getStoredClient();
            if (client != null) {
                this.toBeUsedUser = client;
            } else {
                this.toBeUsedUser = this.user;
            }
        } else {
            this.toBeUsedUser = this.user;
        }
        if (this.user.isAdvisor) {
            // Populate commercial/residential data based on client
            this.account.company = this.toBeUsedUser.company;
        }
        this.account.user = this.user;
        this.account.billImages = [];
        this.consumptions = [];
        this.reloadSelects();
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    fillAccountUsingUserData(): void {
        this.account.serviceName = this.toBeUsedUser.firstName + " " + this.toBeUsedUser.lastName;
        this.account.addressLine1 = this.toBeUsedUser.addressLine1;
        this.account.addressLine2 = this.toBeUsedUser.addressLine2;
        this.account.state = this.toBeUsedUser.state;
        this.account.city = this.toBeUsedUser.city;
        this.account.zip = this.toBeUsedUser.zip;
    }

    goToStart(): void {
        this.clearErrors();
        this.step = 1;
        this.refreshView();
    }

    nextStep(): void {
        this.clearErrors();
        if (this.step == 1) {
            if (this.imageMode == 0 || this.imageMode == 2) {
                this.parseBill();
                return;
            }
            if (!this.account.rateClass?.isCommercial) {
                this.fillAccountUsingUserData();
            }
            this.step = 2;
            this.openExampleDialog(true);
            var t = this;
            setTimeout(function () { t.serviceName.nativeElement.focus() }, 100);
            this.refreshView();
            return;
        }
    }

    finishStep2(next: boolean): void {
        if (!next) {
            this.goToStart();
            return;
        }
        this.step = 3;
        this.nextStep();
    }

    finishStep3(next: boolean): void {
        if (!next) {
            this.gotoStep(2);
            return;
        }
        this.confirm();
    }

    parseBill(): void {
        if (this.imageMode == 0 && this.billsToUpload == null) {
            this.addError("billToUpload");
            this.setErrorText("Please select a bill in order to add your account.");
            return;
        }
        if (this.imageMode == 2 && this.billsToUpload == null) {
            this.setErrorText("Please make a photo of your bill in order to add your account.");
            return;
        }

        this.loading = true;
        this.progress = true;
        this.refreshView();

        var t = this;
        this.progressWidth = 0;
        this.clearErrors();
        setTimeout(() => {
            t.progressWidth = 100;
            t.progress = false;
            t.cdr.markForCheck();
        }, environment.BILL_PARSE_LENGTH);
        this.disableSelectorOfUtilityData = false;
        this.generalService.parseBill(this.billsToUpload).subscribe(templateAccountData => {
            this.loading = false;
            var templateAccount = templateAccountData.key;
            this.consumptions = templateAccountData.value;
            this.account.utility = templateAccount.utility;
            this.account.loadZone = templateAccount.loadZone;
            this.account.rateClass = templateAccount.rateClass;
            this.account.company = templateAccount.company;
            this.account.serviceName = templateAccount.serviceName;
            this.account.addressLine1 = templateAccount.addressLine1;
            this.account.addressLine2 = templateAccount.addressLine2;
            this.account.state = templateAccount.state;
            this.account.city = templateAccount.city;
            this.account.zip = templateAccount.zip;
            this.account.accountNumber = templateAccount.accountNumber;
            this.account.keyName = templateAccount.keyName;
            this.account.billImages = templateAccount.billImages;
            this.account.company = templateAccount.company;
            if (this.account.utility == null) {
                this.setErrorText("The utility bill uploaded is not recognized as a Rhode Island Energy account. Please upload a qualifying Rhode Island Energy bill.");
                this.billsToUpload = [];
                this.refreshView();
                return;
            }
            this.step = 2;
            this.generalService.ifAccountNumberUsed(this.account.utility, this.currentAccountNumber.value, null).then(res => {
                if (res.value) {
                    this.addError("accountNumber");
                    this.setErrorText("Account with such number already exists.");
                } else if (res.key) {
                    this.addError("accountNumber");
                    this.setErrorText("accountissue");
                }
                this.refreshView();
            });
            this.loaderService.setDisabled(false);
            this.refreshView();
            return;
        });
    }

    confirm(): void {
        this.clearErrors();
        this.generalService.addAccount(this.account, this.existingContractId, this.existingContract, this.consumptions, [], this.fileToUpload).subscribe({
            next: (res: number | null) => {
                this.generalService.clearUserCompaniesFromSession();
                if (res != null && res > 0) {
                    this.account.id = res;
                    this.finish(false);
                }
            },
            error: (err: any) => {
                if (err.status == 400) {
                    this.setErrorText(err.error);
                    this.refreshView();
                }
            }
        });

    }

    gotoStep(num: number): void {
        this.step = num;
        this.clearErrors();
        this.refreshView();
    }

    finish(withPopup: boolean): void {
        var t = this;
        if (!withPopup) {
            t.generalService.switchStoredAccount(this.account);
            t.goto("/user/home");
            return;
        }
        this.openResultsPopup("Your new account has been created", "This account is subscribed to Nonstop Shop ai Level 1. You can modify or deactivate your subscription at any time by going to Main Menu > AI Tasks.", () => {
            t.generalService.switchStoredAccount(this.account);
            t.goto("/user/home");
        });
    }

    reloadSelects(): void {
        if (this.utilityId != null && this.utilityId > 0) {
            let temp = this.account;
            let currentUtility = this.utilities[EntityBase.indexOf(this.utilities, new Utility(this.utilityId))];
            this.account.utility = currentUtility;
            this.currentAccountNumber.setValue(this.account.accountNumber);
            if (temp != null && currentUtility != null) {
                this.generalService.getRateClasses(currentUtility).then((rateClasses: any) => {
                    this.rateClasses = [];
                    for (let i = 0; i < rateClasses.length; i++) {
                        this.rateClasses.push(rateClasses[i]);
                    }
                    if (this.rateClasses.length == 1) {
                        this.account.rateClass = this.rateClasses[0];
                    }
                    for (let i = 0; i < this.rateClasses.length; i++) {
                        var isResidential = true;
                        if (this.toBeUsedUser.company != null) {
                            isResidential = false;
                        }
                        if (isResidential && !this.rateClasses[i].isCommercial && currentUtility != null && currentUtility.defaultRateClassForResidential != null && currentUtility.defaultRateClassForResidential.id == this.rateClasses[i].id
                            ||
                            !isResidential && this.rateClasses[i].isCommercial && currentUtility != null && currentUtility.defaultRateClassForCommercial != null && currentUtility.defaultRateClassForCommercial.id == this.rateClasses[i].id
                        ) {
                            this.account.rateClass = this.rateClasses[i];
                        }
                    }
                    this.refreshView();
                });
                this.generalService.getLoadZones(currentUtility).then((loadZones: any) => {
                    this.loadZones = loadZones;
                    if (this.loadZones.length == 1) {
                        this.account.loadZone = this.loadZones[0];
                    }
                    for (let i = 0; i < this.loadZones.length; i++) {
                        if (currentUtility.defaultLoadZone != null && currentUtility.defaultLoadZone.id == this.loadZones[i].id) {
                            this.account.loadZone = this.loadZones[i];
                        }
                    }
                    this.refreshView();
                });
            }
        }
    }

    handleFileInput(event: Event) {
        this.loaderService.setDisabled(true);
        const target = event.target as HTMLInputElement;
        if (this.step == 1) {
            this.billsToUpload = [];
            this.billsToUpload.push((target.files as FileList).item(0) as File);
            this.resetError("billToUpload");
            this.nextStep();
            return;
        }
        this.fileToUpload = (target.files as FileList).item(0) as File;
        this.resetError("fileToUpload");
        this.refreshView()
    }

    goBack(): void {
        this.router.navigate(["/user/home"]);
    }

    openExampleDialog(firstTime: boolean): void {
        if (firstTime && !this.defaultShowAccountInfo) {
            return;
        }
        this.defaultShowAccountInfo = false;
        let dialogRef = this.dialog.open(AccountExampleDialog, {
            disableClose: false
            , panelClass: "example-popup"
        });
        var t = this;
        dialogRef.afterClosed().subscribe(result => {
            t.serviceName.nativeElement.focus();
        });
    }

    canCapture(): boolean {
        return (navigator.mediaDevices != null);
    }

    setTaxtCertificate(certificateFile: File | null) {
        this.fileToUpload = certificateFile;
    }
    setDeleteTaxExemptCertificateChanged(deleteTaxExemptCertificateDocument: boolean): void {
        this.deleteTaxExemptCertificateDocument = deleteTaxExemptCertificateDocument;
    }
    setExistingContractId(existingContractId: number|null): void {
        this.existingContractId = existingContractId;
    }
    setExistingContract(existingContract: ExistingContract|null): void {
        this.existingContract = existingContract;
    }
}
